import { useMemo } from "react";
import type { SelectorItem } from "@ppb/the-wall-common/types";
import { getEventRegistry } from "eventemitter3-singleton";
import type { EventSwitcherCardEvents } from "./events";

type ViewLink = {
  viewUrn: string;
  viewUrl: string;
};

type ViewLinks = {
  [id: string]: ViewLink | undefined;
};

export type EventSwitcherItem = {
  viewUrn: string;
  viewUrl: string;
  label: string;
};

const { emit } = getEventRegistry<EventSwitcherCardEvents>();

const onClose = (urn: string, viewLink: ViewLink) => {
  emit("@@UI/GENERIC_SWITCHER_CLOSE", { urn, viewLink });
};

const onOpen = (urn: string) => {
  emit("@@UI/EVENT_SWITCHER_OPEN", { urn });
};

const events = {
  onClose,
  onOpen,
};

/**
 * Builds the `Selector` payload for the competition switcher. Mirrors `RaceSwitcherCard.viewmodel`: items keyed
 * by view urn, a `links` map for the selection target, and the current event as the default — appended to
 * `items` when the siblings don't contain it, with no `links` entry, so re-selecting it is a no-op.
 */
export function useEventSwitcherCardVM(currentViewUrn: string, currentLabel: string, siblings: EventSwitcherItem[]) {
  // Memoised so `Selector` keeps its item list across the host's re-renders — it is unmemoised
  // itself, and web has no compiler (AGENTS.md).
  const data = useMemo(() => {
    const items: SelectorItem[] = [];
    const links: ViewLinks = {};
    let currentExists = false;

    for (const sibling of siblings) {
      items.push({ id: sibling.viewUrn, label: sibling.label });
      links[sibling.viewUrn] = { viewUrn: sibling.viewUrn, viewUrl: sibling.viewUrl };
      currentExists = currentExists || sibling.viewUrn === currentViewUrn;
    }

    if (!currentExists) {
      items.push({ id: currentViewUrn, label: currentLabel });
    }

    return {
      title: currentLabel,
      defaultValue: { id: currentViewUrn, label: currentLabel },
      items,
      links,
    };
  }, [currentViewUrn, currentLabel, siblings]);

  return {
    vm: {
      data,
      events,
    },
  };
}
